import { Box, Flex, Text } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { customPaddingStyles } from 'containers/Integrations/shared/react-select-custom';
import { useIntegrationSyncPolling } from 'hooks/common/useIntegrationSyncPolling';
import {
  IXeroEmployeeSearchQuery,
  useFetchXeroPaginatedEmployee,
} from 'hooks/integrations/useFetchPaginatedXeroEmployees';
import { fetchXeroTimeeroPaginatedEmployee } from 'hooks/integrations/useFetchPaginatedXeroTimeeroEmployees';
import { IXeroEmployees, useImportXeroEmployee } from 'hooks/integrations/useImportXeroEmployee';
import { IXeroEmployee, useMapXeroEmployee } from 'hooks/integrations/useMapXeroEmployee';
import { useSyncXero } from 'hooks/integrations/useSyncXero';
import { IXeroUnmap, useUnmapXeroMapping } from 'hooks/integrations/useUnMapXeroMapping';
import { getAsyncSelectLoadOptions } from 'utils/helpers';
import toaster from 'utils/toaster';

import EmployeeMappingTableFooter from './EmployeeMappingTableFooter';

const initialSearchQuery: IXeroEmployeeSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const EmployeeMapping = () => {
  const [mappedUsers, setMappedUsers] = useState<any>(null);
  const [currentMapUser, setCurrentMapUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IXeroEmployeeSearchQuery>(initialSearchQuery);

  const { t } = useTranslation(['common', 'integrations', 'employees']);

  const { data: xeroEmployeeData, status: xeroEmployeeStatus, refetch } = useFetchXeroPaginatedEmployee(searchQuery);
  const { status: xeroImportEmployeesStatus, mutateAsync: xeroImportEmployeesMutateAsync } = useImportXeroEmployee();
  const { status: xeroMapEmployeeStatus, mutateAsync: xeroMapEmployeeMutateAsync } = useMapXeroEmployee();
  const { status: xeroUnmapEmployeeStatus, mutateAsync: xeroUnmapEmployeeMutateAsync } = useUnmapXeroMapping();
  const {
    status: xeroSyncStatus,
    mutateAsync: xeroSyncMutateAsync,
    data: syncData,
    isError: isSyncError,
  } = useSyncXero();

  const { syncLongPollingStatus } = useIntegrationSyncPolling({ id: syncData?.payload?.id, refetch: refetch });

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />;
      },
    },
    {
      Header: t('integrations:xero.xeroEmployees'),
      accessor: 'name',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.name}</Text>;
      },
    },
    {
      Header: t('integrations:xero.xeroEmail'),
      accessor: 'email',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.selected_user?.email}</Text>;
      },
    },
    {
      Header: t('integrations:timeeroEmployees'),
      accessor: 'list',
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={timeeroEmployeeLoadOptions}
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.selected_user
                ? data.row.original?.selected_user || null
                : mappedUsers &&
                    mappedUsers[`${data.row.original.id}`] &&
                    mappedUsers[`${data.row.original.id}`].employee
                  ? mappedUsers[`${data.row.original.id}`].employee
                  : null
            }
            onChange={(value: any) => {
              setMappedUsers((prevValue: any) => ({
                ...prevValue,
                [data.row.original.id]: {
                  employee: value || null,
                },
              }));
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('integrations:selectEmployees')}
            styles={customPaddingStyles}
            formatOptionLabel={formatOptionLabel}
            getOptionValue={(option: any) => option.userId}
          />
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 100,
      Cell: (data: CellValue) => {
        return data.row.original.selected_user && data.row.original.selected_user ? (
          <Button
            _hover={{ bg: 'secondary.100' }}
            fontWeight="bold"
            background="rgba(250, 85, 85, 0.2)"
            color="rgba(250, 85, 85, 1)"
            isLoading={xeroUnmapEmployeeStatus === 'loading'}
            onClick={() => onUnMapEmployees(data.row.original.id)}
          >
            {t('integrations:removeMapping')}
          </Button>
        ) : (
          <Button
            _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
            background="rgba(255, 103, 32, 0.1)"
            colorScheme="rgba(255, 103, 32, 0.3)"
            color="secondary.500"
            isLoading={xeroMapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id}
            onClick={() => onMapEmployees(data.row.original.id)}
          >
            {t('integrations:mapEmployee')}
          </Button>
        );
      },
    },
  ];

  const formatOptionLabel = (option: any) => {
    return (
      <Box>
        <Text fontSize="sm" fontWeight={600}>
          {option.firstName + ' ' + option.lastName}
        </Text>
        <Text fontSize="sm" fontWeight={400}>
          {option.email}
        </Text>
      </Box>
    );
  };

  const onMapEmployees = (gustoId: number) => {
    if (mappedUsers && mappedUsers[gustoId] && mappedUsers[gustoId].hasOwnProperty('employee')) {
      setCurrentMapUser(gustoId);
      const postData: IXeroEmployee = {
        xero_employee_id: gustoId,
        user_id: parseInt(mappedUsers[gustoId].employee.id),
      };
      xeroMapEmployeeMutateAsync(postData).then(() => {
        refetch();
      });
    } else {
      toaster.error({ description: t('integrations:employeeSelectMapError') });
    }
  };

  const onUnMapEmployees = (xeroId: number) => {
    setCurrentMapUser(xeroId);
    const postData: IXeroUnmap = {
      xero_employee_id: xeroId,
    };
    xeroUnmapEmployeeMutateAsync(postData).then(() => {
      setCurrentMapUser(null);
      setMappedUsers((prevValue: any) => ({
        ...prevValue,
        [xeroId]: null,
      }));
      refetch();
    });
  };

  const onImportEmployee = (selectedIds: Array<number>) => {
    const postData: IXeroEmployees = {
      xero_employee_ids: selectedIds,
    };
    xeroImportEmployeesMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const onSyncGusto = () => {
    xeroSyncMutateAsync();
    syncLongPollingStatus.current = 'fetching';
  };

  const timeeroEmployeeLoadOptions = async (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchXeroTimeeroPaginatedEmployee, { search, page });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IXeroEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    const updatedSearchQuery = (previousSearchQuery: IXeroEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page_size: pageSize,
      page: 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <Box>
      <Title>{t('integrations:xero.xeroMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={!isSyncError && (xeroSyncStatus === 'loading' || syncLongPollingStatus.current === 'fetching')}
          onClick={onSyncGusto}
        >
          {t('integrations:xero.syncOnline')}
        </Button>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: t('integrations:searchEmployees'),
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <Table
        columns={columns}
        data={xeroEmployeeData?.payload || []}
        isLoading={xeroEmployeeStatus === 'loading'}
        enableRowSelect
        paginationData={xeroEmployeeData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <EmployeeMappingTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleImportEmployee={onImportEmployee}
              isLoading={xeroImportEmployeesStatus === 'loading'}
              t={t}
            />
          );
        }}
      />
    </Box>
  );
};
export default EmployeeMapping;
